import React from "react";
import { OverviewSection } from "./overview-section";
import { EngagementOverview } from "./engagement-overview";
import { PartnerReviewDashboard } from "./partner-review-dashboard";
import { PartnerReviewPlanning } from "./partner-review/PartnerReviewPlanning";
import { PartnerReviewConcluding } from "./partner-review/PartnerReviewConcluding";
import { DetailedPlanningReviewDashboard } from "./partner-review/DetailedPlanningReviewDashboard";
import { DetailedConcludingReviewDashboard } from "./partner-review/DetailedConcludingReviewDashboard";
import { EqrChecklist } from "./eqr-review/EqrChecklist";
import { EqrDashboard } from "./eqr-review/EqrDashboard";
import { FinancialRatiosSection } from "./financial-ratios-section";
import { BalanceSheetSection } from "./balance-sheet-section";
import { TrialBalanceSection } from "./trial-balance-section";
import { AuditEntriesSection } from "./audit-entries-section";
import { ProfitLossSection } from "./profit-loss-section";
import { ChartsSection } from "./charts-section";
import DocumentsPage from "./documents/DocumentsPage";
import AssignedDocumentsPage from "./documents/AssignedDocumentsPage";
import RecycleBinPage from "./documents/RecycleBinPage";
import LibrariesPage from "./libraries/LibrariesPage";
import RommLibraryPage from "../pages/RommLibraryPage";
import Diagnostics from "./utility/Diagnostics";
import Archival from "./utility/Archival";
import AccessAndRoles from "./utility/AccessAndRoles";
import CustomRollforward from "./utility/CustomRollforward";
import PayrollRunner from "./payroll/PayrollRunner";
import PayrollLanding from "./payroll/PayrollLanding";
import PayrollRomms from "./payroll/PayrollRomms";
import WorkspaceDeficiencies from "./payroll/WorkspaceDeficiencies";
import WorkspaceMisstatements from "./payroll/WorkspaceMisstatements";
import { WORKSPACE_DEFICIENCY_ROUTES } from "../utils/workspace-deficiency-routes";

// Simple landing view for Execution category before a workspace is chosen
const ExecutionLanding: React.FC = () => {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-lg w-full">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-8 backdrop-blur-sm">
          <div className="relative text-center space-y-4">
            {/* Icon circle */}
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/5 border border-white/10 mb-4">
              <svg
                className="h-8 w-8 text-white/70"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Execution
            </h2>
            <p className="text-sm text-white/70 leading-relaxed max-w-md mx-auto">
              Choose a workspace from the Execution list on the left panel to view and manage your audit procedures, controls and substantive testing.
            </p>
            
            {/* Decorative dots */}
            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="h-1.5 w-1.5 rounded-full bg-white/30" />
              <div className="h-1.5 w-1.5 rounded-full bg-white/40" />
              <div className="h-1.5 w-1.5 rounded-full bg-white/30" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple landing view for Conclusion category before a workspace is chosen
const ConclusionLanding: React.FC = () => {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-lg w-full">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-8 backdrop-blur-sm">
          <div className="relative text-center space-y-4">
            {/* Icon circle */}
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/5 border border-white/10 mb-4">
              <svg
                className="h-8 w-8 text-white/70"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Concluding Procedures
            </h2>
            <p className="text-sm text-white/70 leading-relaxed max-w-md mx-auto">
              Choose a workspace from the Conclusion list on the left panel to view and manage your audit procedures, controls and substantive testing.
            </p>
            
            {/* Decorative dots */}
            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="h-1.5 w-1.5 rounded-full bg-white/30" />
              <div className="h-1.5 w-1.5 rounded-full bg-white/40" />
              <div className="h-1.5 w-1.5 rounded-full bg-white/30" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
import PPELanding from "./ppe/PPELanding";
import PPETailoringQuestions from "./payroll/ppe/PPETailoringQuestions";
import PPERomms from "./payroll/ppe/PPERomms";
import PPEInternalControls from "./payroll/ppe/PPEInternalControls";
import PPESubstantiveProcedures from "./payroll/ppe/PPESubstantiveProcedures";
import PPEIPETesting from "./payroll/ppe/PPEIPETesting";
import PPEExceptionTesting from "./payroll/ppe/PPEExceptionTesting";
import PPECwipAnalysis from "./payroll/ppe/PPECwipAnalysis";
import PPEAdditionsDeletions from "./payroll/ppe/PPEAdditionsDeletions";
import PPENonCurrentAssetsHeldForSale from "./payroll/ppe/PPENonCurrentAssetsHeldForSale";
import BAPBLanding from "./payroll/bapb/BAPBLanding";
import BAPBTailoringQuestions from "./payroll/bapb/BAPBTailoringQuestions";
import BAPBRomms from "./payroll/bapb/BAPBRomms";
import BAPBInternalControls from "./payroll/bapb/BAPBInternalControls";
import BAPBSubstantiveProcedures from "./payroll/bapb/BAPBSubstantiveProcedures";
import BAPBIPETesting from "./payroll/bapb/BAPBIPETesting";
import BAPBValuationTesting from "./payroll/bapb/BAPBValuationTesting";
import BAPBPhysicalVerification from "./payroll/bapb/BAPBPhysicalVerification";
import BAPBDisclosureTesting from "./payroll/bapb/BAPBDisclosureTesting";
import {
  ControlsPanel,
  TailoringQuestionsPanel,
} from "./payroll/PlaceholderPanels";
import WorkspaceInternalControls from "./libraries/WorkspaceInternalControls";
import SubstantiveProcedures from "./payroll/SubstantiveProcedures";
import IPETesting from "./payroll/substantive-procedures/IPETesting";
import ExceptionTesting from "./payroll/substantive-procedures/ExceptionTesting";
import HeadcountReconciliation from "./payroll/substantive-procedures/HeadcountReconciliation";
import MoMAnalysis from "./payroll/substantive-procedures/MoMAnalysis";
import IncrementAnalysis from "./payroll/substantive-procedures/IncrementAnalysis";
import SalaryAnalytical from "./payroll/substantive-procedures/SalaryAnalytical";
import ActuaryTesting from "./payroll/substantive-procedures/ActuaryTesting";
import ManagerialRemuneration from "./payroll/substantive-procedures/ManagerialRemuneration";
import ShareBasedPayment from "./payroll/substantive-procedures/ShareBasedPayment";
import CustomProcedure from "./substantive-procedures/CustomProcedure";
import { PlanningWorkflow } from "./planning/planning-workflow";
import { PlanningSection } from "./planning/planning-section";
import { MaterialitySection } from "./planning/materiality-section";
import { PreliminaryAnalyticsSection } from "./planning/preliminary-analytics-section";
import EngagementAcceptanceLanding from "./planning/EngagementAcceptanceLanding";
import UnderstandingEntityLanding from "./planning/UnderstandingEntityLanding";
import MaterialityLanding from "./planning/MaterialityLanding";
import PreliminaryAnalyticalLanding from "./planning/PreliminaryAnalyticalLanding";
import FraudRiskLanding from "./planning/FraudRiskLanding";
import ITRiskLanding from "./planning/ITRiskLanding";
import IndependenceLanding from "./planning/IndependenceLanding";
import { logger } from "../utils/logger";
import IntangibleAssetsLanding from "./intangible/IntangibleAssetsLanding";
import IATailoringQuestions from "./intangible/IATailoringQuestions";
import IAIPETesting from "./intangible/IAIPETesting";
import IAExceptionTesting from "./intangible/IAExceptionTesting";
import IAAdditionsDeletions from "./intangible/IAAdditionsDeletions";
import IAUnderDevelopment from "./intangible/IAUnderDevelopment";
import IARomms from "./intangible/IARomms";
import IASubstantiveProcedures from "./intangible/IASubstantiveProcedures";
import IntangibleAssetsInternalControls from "./intangible/IntangibleAssetsInternalControls";
import DepreciationLanding from "./intangible/DepreciationLanding";
import BorrowingsLanding from "./intangible/Borr/BorrowingsLanding";
import BorrowingsIndASLanding from "./intangible/Borr/BorrowingsIndASLanding";
// Concluding - CARO placeholder (to be provided later by user)
import CAROLanding from "./concluding/CAROLanding";
import CAROClausePage from "./concluding/CAROClausePage";
import AuditReportAndOpinionLanding from "./concluding/AuditReportAndOpinionLanding";
import CommunicationWithGovernanceLanding from "./concluding/CommunicationWithGovernanceLanding";
import SubsequentEventsLanding from "./concluding/SubsequentEventsLanding";
import WrittenRepresentationsLanding from "./concluding/WrittenRepresentationsLanding";
import LitigationsAndClaimsLanding from "./concluding/LitigationsAndClaimsLanding";
import GoingConcernConcludingLanding from "./concluding/GoingConcernLanding";
import ComplianceWithLawsAndRegulationsConcludingLanding from "./concluding/ComplianceWithLawsAndRegulationsLanding";
import ReviewOfEstimatesForManagementBiasConcludingLanding from "./concluding/ReviewOfEstimatesForManagementBiasLanding";
import InternalAuditConcludingLanding from "./concluding/InternalAuditLanding";
import EvaluateAuditResultsLanding from "./concluding/EvaluateAuditResultsLanding";
import OtherExpensesLanding from "./intangible/OthExp/OtherExpensesLanding";
import OthExpTailoringQuestions from "./intangible/OthExp/OthExpTailoringQuestions";
import OthExpRomms from "./intangible/OthExp/OthExpRomms";
import OthExpInternalControls from "./intangible/OthExp/OthExpInternalControls";
import OthExpSubstantive from "./intangible/OthExp/OthExpSubstantive";
import OthExpSubstantiveProcedures from "./intangible/OthExp/OthExpSubstantiveProcedures";
import COMCLanding from "./intangible/COMC/COMCLanding";
import COMCTailoringQuestions from "./intangible/COMC/COMCTailoringQuestions";
import COMCRomms from "./intangible/COMC/COMCRomms";
import COMCInternalControls from "./intangible/COMC/COMCInternalControls";
import COMCSubstantive from "./intangible/COMC/COMCSubstantive";
import COMCSubstantiveProcedures from "./intangible/COMC/COMCSubstantiveProcedures";
import COMCIPETesting from "./intangible/COMC/COMCIPETesting";
import COMCExceptionTesting from "./intangible/COMC/COMCExceptionTesting";
import COMCPurchaseAnalyticalDashboard from "./intangible/COMC/COMCPurchaseAnalyticalDashboard";
import DepreciationTailoringQuestions from "./intangible/DepreciationTailoringQuestions";
import DepreciationRomms from "./intangible/DepreciationRomms";
import DepreciationInternalControls from "./intangible/DepreciationInternalControls";
import DepreciationSubstantive from "./intangible/DepreciationSubstantive";
import DepreciationROUSubstantive from "./intangible/DepreciationROUSubstantive";
import DepreciationTangibleAssets from "./intangible/DepreciationTangibleAssets";
import DepreciationIntangibleAssets from "./intangible/DepreciationIntangibleAssets";
import TradePayablesLanding from "./intangible/TrdPbl/TradePayablesLanding";
import TradePayablesTailoringQuestions from "./intangible/TrdPbl/TradePayablesTailoringQuestions";
import TradePayablesRomms from "./intangible/TrdPbl/TradePayablesRomms";
import TradePayablesInternalControls from "./intangible/TrdPbl/TradePayablesInternalControls";
import TradePayablesSubstantiveProcedures from "./intangible/TrdPbl/TradePayablesSubstantiveProcedures";
import TradePayableIPETesting from "./intangible/TrdPbl/TradePayableIPETesting";
import TradePayableExceptionTesting from "./intangible/TrdPbl/TradePayableExceptionTesting";
import InvLanding from "./intangible/Inv/InvLanding";
import InvTailoringQuestions from "./intangible/Inv/InvTailoringQuestions";
import InvRomms from "./intangible/Inv/InvRomms";
import InvInternalControls from "./intangible/Inv/InvInternalControls";
import InvSubstantive from "./intangible/Inv/InvSubstantive";
import InvExceptionTesting from "./intangible/Inv/InvExceptionTesting";
import InvAgeingAnalysis from "./intangible/Inv/InvAgeingAnalysis";
import AgeingAnalysisModule from "./modules/AgeingAnalysisModule";
import InvNRVAssessment from "./intangible/Inv/InvNRVAssessment";
import InvPhysicalVerification from "./intangible/Inv/InvPhysicalVerification";
import InventoryExecutionIPETesting from "./intangible/Inv/InventoryExecutionIPETesting";
import InvCostAllocation from "./intangible/Inv/InvCostAllocation";
import CCELanding from "./intangible/CCE/CCELanding";
import CCETailoringQuestions from "./intangible/CCE/CCETailoringQuestions";
import CCERomms from "./intangible/CCE/CCERomms";
import CCEInternalControls from "./intangible/CCE/CCEInternalControls";
import CCESubstantive from "./intangible/CCE/CCESubstantive";
import CCESubstantiveProcedures from "./intangible/CCE/CCESubstantiveProcedures";
import FinCstLanding from "./intangible/FinCst/FinCstLanding";
import FinCstTailoringQuestions from "./intangible/FinCst/FinCstTailoringQuestions";
import FinCstRomms from "./intangible/FinCst/FinCstRomms";
import FinCstInternalControls from "./intangible/FinCst/FinCstInternalControls";
import FinCstSubstantive from "./intangible/FinCst/FinCstSubstantive";
import IncTxLanding from "./intangible/IncTx/IncTxLanding";
import IncTxTailoringQuestions from "./intangible/IncTx/IncTxTailoringQuestions";
import IncTxRomms from "./intangible/IncTx/IncTxRomms";
import IncTxInternalControls from "./intangible/IncTx/IncTxInternalControls";
import IncTxSubstantiveProcedures from "./intangible/IncTx/IncTxSubstantiveProcedures";
import InvsmtLanding from "./intangible/invsmt/InvsmtLanding";
import InvsmtTailoringQuestions from "./intangible/invsmt/InvsmtTailoringQuestions";
import InvsmtRomms from "./intangible/invsmt/InvsmtRomms";
import InvsmtInternalControls from "./intangible/invsmt/InvsmtInternalControls";
import InvsmtSubstantive from "./intangible/invsmt/InvsmtSubstantive";
import InvsmtSubstantiveProcedures from "./intangible/invsmt/InvsmtSubstantiveProcedures";
import PCLanding from "./intangible/PCL/PCLanding";
import PCLTailoringQuestions from "./intangible/PCL/PCLTailoringQuestions";
import PCLRomms from "./intangible/PCL/PCLRomms";
import PCLInternalControls from "./intangible/PCL/PCLInternalControls";
import PCLSubstantive from "./intangible/PCL/PCLSubstantive";
import PCLSubstantiveProcedures from "./intangible/PCL/PCLSubstantiveProcedures";
import LeaseLiabilityLanding from "./intangible/LsLb/LeaseLiabilityLanding";
import LeaseLiabilityIndASLanding from "./intangible/LsLb/LeaseLiabilityIndASLanding";
import LeaseLiabilityTailoringQuestions from "./intangible/LsLb/LeaseLiabilityTailoringQuestions";
import LeaseLiabilityRomms from "./intangible/LsLb/LeaseLiabilityRomms";
import LeaseLiabilityInternalControls from "./intangible/LsLb/LeaseLiabilityInternalControls";
import LeaseLiabilitySubstantive from "./intangible/LsLb/LeaseLiabilitySubstantive";
import LeaseLiabilityClassicTailoringQuestions from "./intangible/LsLb/LeaseLiabilityClassicTailoringQuestions";
import LeaseLiabilityClassicRomms from "./intangible/LsLb/LeaseLiabilityClassicRomms";
import LeaseLiabilityClassicInternalControls from "./intangible/LsLb/LeaseLiabilityClassicInternalControls";
import LeaseLiabilityClassicSubstantive from "./intangible/LsLb/LeaseLiabilityClassicSubstantive";
import LeaseAssessment from "./intangible/LsLb/LeaseAssessment";
import FinanceLease from "./intangible/LsLb/FinanceLease";
import OperatingLease from "./intangible/LsLb/OperatingLease";
import RightOfUseAssetsLanding from "./intangible/ROU/RightOfUseAssetsLanding";
import RightOfUseAssetsTailoringQuestions from "./intangible/ROU/RightOfUseAssetsTailoringQuestions";
import RightOfUseAssetsRomms from "./intangible/ROU/RightOfUseAssetsRomms";
import RightOfUseAssetsInternalControls from "./intangible/ROU/RightOfUseAssetsInternalControls";
import RightOfUseAssetsSubstantive from "./intangible/ROU/RightOfUseAssetsSubstantive";
import RightOfUseDisclosureProcedure from "./intangible/ROU/RightOfUseDisclosureProcedure";
import LeaseAssessmentIndAS from "./intangible/LsLb/LeaseAssessmentIndAS";
import LeaseTestingIndAS from "./intangible/LsLb/LeaseTestingIndAS";
import LeaseReassessmentIndAS from "./intangible/LsLb/LeaseReassessmentIndAS";
import LeaseModificationIndAS from "./intangible/LsLb/LeaseModificationIndAS";
import LeaseAccountingExceptionIndAS from "./intangible/LsLb/LeaseAccountingExceptionIndAS";
import LeaseDisclosureIndAS from "./intangible/LsLb/LeaseDisclosureIndAS";
import LeaseEmptyProcedure from "./intangible/LsLb/LeaseEmptyProcedure";
import ImpairmentLanding from "./intangible/ImpairmentLanding";
import ImpairmentTailoringQuestions from "./intangible/ImpairmentTailoringQuestions";
import ImpairmentRomms from "./intangible/ImpairmentRomms";
import ImpairmentInternalControls from "./intangible/ImpairmentInternalControls";
import ImpairmentSubstantive from "./intangible/ImpairmentSubstantive";
import TradeReceivablesLanding from "./intangible/TrdRcv/TradeReceivablesLanding";
import TrdRcvTailoringQuestions from "./intangible/TrdRcv/TrdRcvTailoringQuestions";
import TrdRcvRomms from "./intangible/TrdRcv/TrdRcvRomms";
import TrdRcvInternalControls from "./intangible/TrdRcv/TrdRcvInternalControls";
import TrdRcvSubstantive from "./intangible/TrdRcv/TrdRcvSubstantive";
import TrdRcvIPETesting from "./intangible/TrdRcv/TrdRcvIPETesting";
import TrdRcvExceptionTesting from "./intangible/TrdRcv/TrdRcvExceptionTesting";
import TrdRcvRatioAnalysis from "./intangible/TrdRcv/TrdRcvRatioAnalysis";
import TrdRcvEclFlowRateYoy from "./intangible/TrdRcv/TrdRcvEclFlowRateYoy";
import TrdRcvEclFlowRateQoq from "./intangible/TrdRcv/TrdRcvEclFlowRateQoq";
import TrdPblRatioAnalysis from "./intangible/TrdPbl/TrdPblRatioAnalysis";
import BorrTailoringQuestions from "./intangible/Borr/BorrTailoringQuestions";
import BorrIndASTailoringQuestions from "./intangible/Borr/BorrIndASTailoringQuestions";
import BorrRomms from "./intangible/Borr/BorrRomms";
import BorrInternalControls from "./intangible/Borr/BorrInternalControls";
import BorrSubstantive from "./intangible/Borr/BorrSubstantive";
import BorrSubstantiveProcedures from "./intangible/Borr/BorrSubstantiveProcedures";
import BorrIndASRomms from "./intangible/Borr/BorrIndASRomms";
import BorrIndASInternalControls from "./intangible/Borr/BorrIndASInternalControls";
import BorrIndASSubstantive from "./intangible/Borr/BorrIndASSubstantive";
import BorrIndASSubstantiveProcedures from "./intangible/Borr/BorrIndASSubstantiveProcedures";
import OtherAssetsLanding from "./intangible/OthAss/OtherAssetsLanding";
import OtherAssetsTailoringQuestions from "./intangible/OthAss/OtherAssetsTailoringQuestions";
import OtherAssetsRomms from "./intangible/OthAss/OtherAssetsRomms";
import OtherAssetsInternalControls from "./intangible/OthAss/OtherAssetsInternalControls";
import OtherAssetsSubstantive from "./intangible/OthAss/OtherAssetsSubstantive";
import OtherAssetsIPETesting from "./intangible/OthAss/OtherAssetsIPETesting";
import OtherAssetsPrepaidExpense from "./intangible/OthAss/OtherAssetsPrepaidExpense";
import OtherFinancialAssetsLanding from "./intangible/OFA/OtherFinancialAssetsLanding";
import OtherFinancialAssetsTailoringQuestions from "./intangible/OFA/OtherFinancialAssetsTailoringQuestions";
import OtherFinancialAssetsRomms from "./intangible/OFA/OtherFinancialAssetsRomms";
import OtherFinancialAssetsInternalControls from "./intangible/OFA/OtherFinancialAssetsInternalControls";
import OtherFinancialAssetsSubstantive from "./intangible/OFA/OtherFinancialAssetsSubstantive";
import OtherFinancialAssetsSecurityDeposits from "./intangible/OFA/OtherFinancialAssetsSecurityDeposits";
import OtherIncomeLanding from "./intangible/OthInc/OtherIncomeLanding";
import OtherIncomeTailoringQuestions from "./intangible/OthInc/OtherIncomeTailoringQuestions";
import OtherIncomeRomms from "./intangible/OthInc/OtherIncomeRomms";
import OtherIncomeInternalControls from "./intangible/OthInc/OtherIncomeInternalControls";
import OtherIncomeSubstantive from "./intangible/OthInc/OtherIncomeSubstantive";
import OtherIncomeNetInvestmentOperatingLease from "./intangible/OthInc/OtherIncomeNetInvestmentOperatingLease";
import OtherLiabilitiesLanding from "./intangible/OthLib/OtherLiabilitiesLanding";
import OtherLiabilitiesTailoringQuestions from "./intangible/OthLib/OtherLiabilitiesTailoringQuestions";
import OtherLiabilitiesRomms from "./intangible/OthLib/OtherLiabilitiesRomms";
import OtherLiabilitiesInternalControls from "./intangible/OthLib/OtherLiabilitiesInternalControls";
import OtherLiabilitiesSubstantive from "./intangible/OthLib/OtherLiabilitiesSubstantive";
import AdvanceFromCustomerCompleteness from "./intangible/OthLib/AdvanceFromCustomerCompleteness";
import CapexVendorsCompleteness from "./intangible/OthLib/CapexVendorsCompleteness";
import OtherFinancialLiabilitiesLanding from "./intangible/OthFinLib/OtherFinancialLiabilitiesLanding";
import OtherFinancialLiabilitiesTailoringQuestions from "./intangible/OthFinLib/OtherFinancialLiabilitiesTailoringQuestions";
import OtherFinancialLiabilitiesRomms from "./intangible/OthFinLib/OtherFinancialLiabilitiesRomms";
import OtherFinancialLiabilitiesInternalControls from "./intangible/OthFinLib/OtherFinancialLiabilitiesInternalControls";
import OtherFinancialLiabilitiesSubstantive from "./intangible/OthFinLib/OtherFinancialLiabilitiesSubstantive";
import CapexVendorsCompletenessOFL from "./intangible/OthFinLib/CapexVendorsCompletenessOFL";
import OtherReceivablesLanding from "./intangible/OthRec/OtherReceivablesLanding";
import OtherReceivablesTailoringQuestions from "./intangible/OthRec/OtherReceivablesTailoringQuestions";
import OtherReceivablesRomms from "./intangible/OthRec/OtherReceivablesRomms";
import OtherReceivablesInternalControls from "./intangible/OthRec/OtherReceivablesInternalControls";
import OtherReceivablesSubstantive from "./intangible/OthRec/OtherReceivablesSubstantive";
import AdvanceToSuppliersCompleteness from "./intangible/OthRec/AdvanceToSuppliersCompleteness";
import CapitalAdvancesCompleteness from "./intangible/OthRec/CapitalAdvancesCompleteness";
import GoingConcernLanding from "./intangible/GoLib/GoingConcernLanding";
import GoingConcernTailoringQuestions from "./intangible/GoLib/GoingConcernTailoringQuestions";
import GoingConcernRomms from "./intangible/GoLib/GoingConcernRomms";
import GoingConcernInternalControls from "./intangible/GoLib/GoingConcernInternalControls";
import GoingConcernSubstantive from "./intangible/GoLib/GoingConcernSubstantive";
import GoingConcernSubstantiveQuestions from "./intangible/GoLib/GoingConcernSubstantiveQuestions";
import LitigationsClaimsLanding from "./intangible/LitClm/LitigationsClaimsLanding";
import LitigationsClaimsTailoringQuestions from "./intangible/LitClm/LitigationsClaimsTailoringQuestions";
import LitigationsClaimsRomms from "./intangible/LitClm/LitigationsClaimsRomms";
import LitigationsClaimsInternalControls from "./intangible/LitClm/LitigationsClaimsInternalControls";
import LitigationsClaimsSubstantive from "./intangible/LitClm/LitigationsClaimsSubstantive";
import LitigationsClaimsSubstantiveQuestions from "./intangible/LitClm/LitigationsClaimsSubstantiveQuestions";
import LitigationsClaimsAssessment from "./intangible/LitClm/LitigationsClaimsAssessment";
import OpeningBalanceTestingLanding from "./intangible/OpnBal/OpeningBalanceTestingLanding";
import OpeningBalanceTestingTailoringQuestions from "./intangible/OpnBal/OpeningBalanceTestingTailoringQuestions";
import OpeningBalanceTestingRomms from "./intangible/OpnBal/OpeningBalanceTestingRomms";
import OpeningBalanceTestingInternalControls from "./intangible/OpnBal/OpeningBalanceTestingInternalControls";
import OpeningBalanceTestingSubstantive from "./intangible/OpnBal/OpeningBalanceTestingSubstantive";
import OpeningBalanceTestingSubstantiveProcedures from "./intangible/OpnBal/OpeningBalanceTestingSubstantiveProcedures";
import RelatedPartyDisclosureLanding from "./intangible/RelParD/RelatedPartyDisclosureLanding";
import RelatedPartyDisclosureTailoringQuestions from "./intangible/RelParD/RelatedPartyDisclosureTailoringQuestions";
import RelatedPartyDisclosureRomms from "./intangible/RelParD/RelatedPartyDisclosureRomms";
import RelatedPartyDisclosureInternalControls from "./intangible/RelParD/RelatedPartyDisclosureInternalControls";
import RelatedPartyDisclosureSubstantive from "./intangible/RelParD/RelatedPartyDisclosureSubstantive";
import ComplianceSubstantive from "./intangible/RelParD/ComplianceSubstantive";
import ArmsLengthAssessmentSubstantive from "./intangible/RelParD/ArmsLengthAssessmentSubstantive";
import EquityLanding from "./intangible/Eqt/EquityLanding";
import EquityTailoringQuestions from "./intangible/Eqt/EquityTailoringQuestions";
import EquityRomms from "./intangible/Eqt/EquityRomms";
import EquityInternalControls from "./intangible/Eqt/EquityInternalControls";
import EquitySubstantive from "./intangible/Eqt/EquitySubstantive";
import ManagementUseOfExpertLanding from "./intangible/MgtExp/ManagementUseOfExpertLanding";
import ManagementUseOfExpertTailoringQuestions from "./intangible/MgtExp/ManagementUseOfExpertTailoringQuestions";
import ManagementUseOfExpertRomms from "./intangible/MgtExp/ManagementUseOfExpertRomms";
import ManagementUseOfExpertInternalControls from "./intangible/MgtExp/ManagementUseOfExpertInternalControls";
import ManagementUseOfExpertSubstantive from "./intangible/MgtExp/ManagementUseOfExpertSubstantive";
import ManagementUseOfExpertAssessment from "./intangible/MgtExp/ManagementUseOfExpertAssessment";
import UseOfAuditorsSpecialistLanding from "./intangible/UseAdtr/UseOfAuditorsSpecialistLanding";
import UseOfAuditorsSpecialistTailoringQuestions from "./intangible/UseAdtr/UseOfAuditorsSpecialistTailoringQuestions";
import UseOfAuditorsSpecialistRomms from "./intangible/UseAdtr/UseOfAuditorsSpecialistRomms";
import UseOfAuditorsSpecialistInternalControls from "./intangible/UseAdtr/UseOfAuditorsSpecialistInternalControls";
import UseOfAuditorsSpecialistSubstantive from "./intangible/UseAdtr/UseOfAuditorsSpecialistSubstantive";
import ComplianceWithLawsAndRegulationsLanding from "./intangible/CmpLR/ComplianceWithLawsAndRegulationsLanding";
import ComplianceWithLawsAndRegulationsTailoringQuestions from "./intangible/CmpLR/ComplianceWithLawsAndRegulationsTailoringQuestions";
import ComplianceWithLawsAndRegulationsRomms from "./intangible/CmpLR/ComplianceWithLawsAndRegulationsRomms";
import ComplianceWithLawsAndRegulationsInternalControls from "./intangible/CmpLR/ComplianceWithLawsAndRegulationsInternalControls";
import ComplianceWithLawsAndRegulationsSubstantive from "./intangible/CmpLR/ComplianceWithLawsAndRegulationsSubstantive";
import ReviewOfEstimatesForManagementBiasLanding from "./intangible/RvEstMB/ReviewOfEstimatesForManagementBiasLanding";
import ReviewOfEstimatesForManagementBiasTailoringQuestions from "./intangible/RvEstMB/ReviewOfEstimatesForManagementBiasTailoringQuestions";
import ReviewOfEstimatesForManagementBiasRomms from "./intangible/RvEstMB/ReviewOfEstimatesForManagementBiasRomms";
import ReviewOfEstimatesForManagementBiasInternalControls from "./intangible/RvEstMB/ReviewOfEstimatesForManagementBiasInternalControls";
import ReviewOfEstimatesForManagementBiasSubstantive from "./intangible/RvEstMB/ReviewOfEstimatesForManagementBiasSubstantive";
import InternalAuditLanding from "./intangible/IntAdt/InternalAuditLanding";
import InternalAuditTailoringQuestions from "./intangible/IntAdt/InternalAuditTailoringQuestions";
import InternalAuditRomms from "./intangible/IntAdt/InternalAuditRomms";
import InternalAuditInternalControls from "./intangible/IntAdt/InternalAuditInternalControls";
import InternalAuditSubstantive from "./intangible/IntAdt/InternalAuditSubstantive";
import FCRPLanding from "./intangible/Fcrp/FCRPLanding";
import FCRPTailoringQuestions from "./intangible/Fcrp/FCRPTailoringQuestions";
import FCRPRomms from "./intangible/Fcrp/FCRPRomms";
import FCRPInternalControls from "./intangible/Fcrp/FCRPInternalControls";
import FCRPSubstantive from "./intangible/Fcrp/FCRPSubstantive";
import MOCLanding from "./intangible/Moc/MOCLanding";
import MOCTailoringQuestions from "./intangible/Moc/MOCTailoringQuestions";
import MOCRomms from "./intangible/Moc/MOCRomms";
import MOCInternalControls from "./intangible/Moc/MOCInternalControls";
import MOCSubstantive from "./intangible/Moc/MOCSubstantive";
import RevenueFromOperationsLanding from "./intangible/RFO/RevenueFromOperationsLanding";
import RevenueFromOperationsTailoringQuestions from "./intangible/RFO/RevenueFromOperationsTailoringQuestions";
import RevenueFromOperationsRomms from "./intangible/RFO/RevenueFromOperationsRomms";
import RevenueFromOperationsSubstantive from "./intangible/RFO/RevenueFromOperationsSubstantive";
import RFOShipAndBillIPETesting from "./intangible/RFO/RFOShipAndBillIPETesting";
import RFOCostPlusComputation from "./intangible/RFO/RFOCostPlusComputation";

export interface RouteConfig {
  component: React.ComponentType<any>;
  props?: Record<string, any>;
  requiresSetActiveSection?: boolean;
}

/**
 * Configuration object for main content routes.
 * Maps section names to their corresponding components and props.
 */
export const mainContentRoutes: Record<string, RouteConfig> = {
  chat: {
    component: EngagementOverview,
  },
  execution: {
    component: ExecutionLanding,
  },
  concluding: {
    component: ConclusionLanding,
  },
  "engagement-overview": {
    component: EngagementOverview,
  },
  "partner-review-dashboard": {
    component: PartnerReviewDashboard,
  },
  "partner-review-planning": {
    component: PartnerReviewPlanning,
  },
  "partner-review-concluding": {
    component: PartnerReviewConcluding,
  },
  "detailed-planning-review-dashboard": {
    component: DetailedPlanningReviewDashboard,
  },
  "detailed-concluding-review-dashboard": {
    component: DetailedConcludingReviewDashboard,
  },
  "eqr-checklist": {
    component: EqrChecklist,
  },
  "eqr-dashboard": {
    component: EqrDashboard,
  },
  overview: {
    component: OverviewSection,
  },
  "financial-ratios": {
    component: FinancialRatiosSection,
  },
  "balance-sheet": {
    component: BalanceSheetSection,
    props: { searchTerm: true }, // Will be replaced with actual searchTerm
  },
  "trial-balance": {
    component: TrialBalanceSection,
    props: { searchTerm: true }, // Will be replaced with actual searchTerm
  },
  "audit-entries": {
    component: AuditEntriesSection,
    props: { searchTerm: true }, // Will be replaced with actual searchTerm
  },
  "profit-loss": {
    component: ProfitLossSection,
    props: { searchTerm: true }, // Will be replaced with actual searchTerm
  },
  charts: {
    component: ChartsSection,
  },
  libraries: {
    component: LibrariesPage,
  },
  "sampling-library": {
    component: LibrariesPage,
  },
  "information-library": {
    component: LibrariesPage,
  },
  "rcm-library": {
    component: LibrariesPage,
  },
  "risk-library": {
    component: LibrariesPage,
  },
  "control-library": {
    component: LibrariesPage,
  },
  "control-owner-library": {
    component: LibrariesPage,
  },
  "it-elements-library": {
    component: LibrariesPage,
  },
  "business-process-library": {
    component: LibrariesPage,
  },
  "confirmation-library": {
    component: LibrariesPage,
  },
  "review-note-library": {
    component: LibrariesPage,
  },
  "document-library": {
    component: DocumentsPage,
  },
  "assigned-documents": {
    component: AssignedDocumentsPage,
  },
  "deficiency-library": {
    component: LibrariesPage,
  },
  "misstatement-library": {
    component: LibrariesPage,
  },
  "bin": {
    component: RecycleBinPage,
  },
  "recycle-bin": {
    component: RecycleBinPage,
  },
  diagnostics: {
    component: Diagnostics,
  },
  archival: {
    component: Archival,
    requiresSetActiveSection: true,
  },
  "access-and-roles": {
    component: AccessAndRoles,
  },
  "custom-rollforward": {
    component: CustomRollforward,
    requiresSetActiveSection: true,
  },
  "execution-payroll": {
    component: PayrollLanding,
    requiresSetActiveSection: true,
  },
  "execution-ppe": {
    component: PPELanding,
    requiresSetActiveSection: true,
  },
  "execution-bapb": {
    component: BAPBLanding,
    requiresSetActiveSection: true,
  },
  "execution-ia": {
    component: IntangibleAssetsLanding,
    requiresSetActiveSection: true,
  },
  "execution-depreciation": {
    component: DepreciationLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-assets": {
    component: OtherAssetsLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-financial-assets": {
    component: OtherFinancialAssetsLanding,
    requiresSetActiveSection: true,
  },
  "execution-inv": {
    component: InvLanding,
    requiresSetActiveSection: true,
  },
  "execution-cce": {
    component: CCELanding,
    requiresSetActiveSection: true,
  },
  "execution-fincst": {
    component: FinCstLanding,
    requiresSetActiveSection: true,
  },
  "execution-inctx": {
    component: IncTxLanding,
    requiresSetActiveSection: true,
  },
  "execution-invsmt": {
    component: InvsmtLanding,
    requiresSetActiveSection: true,
  },
  "execution-pcl": {
    component: PCLanding,
    requiresSetActiveSection: true,
  },
  "execution-lease-liability": {
    component: LeaseLiabilityLanding,
    requiresSetActiveSection: true,
  },
  "execution-lease-liability-ind-as": {
    component: LeaseLiabilityIndASLanding,
    requiresSetActiveSection: true,
  },
  "execution-right-of-use-assets": {
    component: RightOfUseAssetsLanding,
    requiresSetActiveSection: true,
  },
  "execution-impairment": {
    component: ImpairmentLanding,
    requiresSetActiveSection: true,
  },
  "execution-trade-receivables": {
    component: TradeReceivablesLanding,
    requiresSetActiveSection: true,
  },
  "execution-borrowings": {
    component: BorrowingsLanding,
    requiresSetActiveSection: true,
  },
  "execution-borrowings-ind-as": {
    component: BorrowingsIndASLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-expenses": {
    component: OtherExpensesLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-liabilities": {
    component: OtherLiabilitiesLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-financial-liabilities": {
    component: OtherFinancialLiabilitiesLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-receivables": {
    component: OtherReceivablesLanding,
    requiresSetActiveSection: true,
  },
  "execution-going-concern": {
    component: GoingConcernLanding,
    requiresSetActiveSection: true,
  },
  "execution-litigations-claims": {
    component: LitigationsClaimsLanding,
    requiresSetActiveSection: true,
  },
  "execution-opening-balance-testing": {
    component: OpeningBalanceTestingLanding,
    requiresSetActiveSection: true,
  },
  "execution-related-party-disclosure": {
    component: RelatedPartyDisclosureLanding,
    requiresSetActiveSection: true,
  },
  "execution-equity": {
    component: EquityLanding,
    requiresSetActiveSection: true,
  },
  "execution-management-use-of-expert": {
    component: ManagementUseOfExpertLanding,
    requiresSetActiveSection: true,
  },
  "execution-use-of-auditors-specialist": {
    component: UseOfAuditorsSpecialistLanding,
    requiresSetActiveSection: true,
  },
  "execution-compliance-with-laws-and-regulations": {
    component: ComplianceWithLawsAndRegulationsLanding,
    requiresSetActiveSection: true,
  },
  "execution-review-of-estimates-for-management-bias": {
    component: ReviewOfEstimatesForManagementBiasLanding,
    requiresSetActiveSection: true,
  },
  "execution-internal-audit": {
    component: InternalAuditLanding,
    requiresSetActiveSection: true,
  },
  "execution-fcrp": {
    component: FCRPLanding,
    requiresSetActiveSection: true,
  },
  "execution-moc": {
    component: MOCLanding,
    requiresSetActiveSection: true,
  },
  "execution-comc": {
    component: COMCLanding,
    requiresSetActiveSection: true,
  },
  "execution-other-income": {
    component: OtherIncomeLanding,
    requiresSetActiveSection: true,
  },
  "execution-rfo": {
    component: RevenueFromOperationsLanding,
    requiresSetActiveSection: true,
  },
  // Concluding Procedures
  "concluding-caro": {
    component: CAROLanding,
    requiresSetActiveSection: true,
  },
  "concluding-caro-clause-i": { component: CAROClausePage, props: { clauseId: "i" }, requiresSetActiveSection: true },
  "concluding-caro-clause-ii": { component: CAROClausePage, props: { clauseId: "ii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-iii": { component: CAROClausePage, props: { clauseId: "iii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-iv": { component: CAROClausePage, props: { clauseId: "iv" }, requiresSetActiveSection: true },
  "concluding-caro-clause-v": { component: CAROClausePage, props: { clauseId: "v" }, requiresSetActiveSection: true },
  "concluding-caro-clause-vi": { component: CAROClausePage, props: { clauseId: "vi" }, requiresSetActiveSection: true },
  "concluding-caro-clause-vii": { component: CAROClausePage, props: { clauseId: "vii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-viii": { component: CAROClausePage, props: { clauseId: "viii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-ix": { component: CAROClausePage, props: { clauseId: "ix" }, requiresSetActiveSection: true },
  "concluding-caro-clause-x": { component: CAROClausePage, props: { clauseId: "x" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xi": { component: CAROClausePage, props: { clauseId: "xi" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xii": { component: CAROClausePage, props: { clauseId: "xii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xiii": { component: CAROClausePage, props: { clauseId: "xiii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xiv": { component: CAROClausePage, props: { clauseId: "xiv" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xv": { component: CAROClausePage, props: { clauseId: "xv" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xvi": { component: CAROClausePage, props: { clauseId: "xvi" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xvii": { component: CAROClausePage, props: { clauseId: "xvii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xviii": { component: CAROClausePage, props: { clauseId: "xviii" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xix": { component: CAROClausePage, props: { clauseId: "xix" }, requiresSetActiveSection: true },
  "concluding-caro-clause-xx": { component: CAROClausePage, props: { clauseId: "xx" }, requiresSetActiveSection: true },
  "concluding-audit-report-opinion": {
    component: AuditReportAndOpinionLanding,
  },
  "concluding-communication-governance": {
    component: CommunicationWithGovernanceLanding,
  },
  "concluding-subsequent-events": {
    component: SubsequentEventsLanding,
  },
  "concluding-written-representations": {
    component: WrittenRepresentationsLanding,
  },
  "concluding-litigations-claims": {
    component: LitigationsAndClaimsLanding,
  },
  "concluding-litclm-tailoring": {
    component: LitigationsClaimsTailoringQuestions,
    props: { areaName: "LIT" }, // Use "LIT" for Concluding Procedures to save to ConcludingProcedures_TailoringQuestions_Responses.json
    requiresSetActiveSection: true,
  },
  "concluding-litclm-romms": {
    component: LitigationsClaimsRomms,
    requiresSetActiveSection: true,
  },
  "concluding-litclm-controls": {
    component: LitigationsClaimsInternalControls,
    requiresSetActiveSection: true,
  },
  "concluding-litclm-substantive": {
    component: LitigationsClaimsSubstantive,
    props: { area: "ConcludingProcedures" },
    requiresSetActiveSection: true,
  },
  "concluding-litclm-substantive-litigations-claims": {
    component: LitigationsClaimsSubstantiveQuestions,
    requiresSetActiveSection: true,
  },
  "concluding-litclm-substantive-assessment": {
    component: LitigationsClaimsAssessment,
    requiresSetActiveSection: true,
  },
  "concluding-litclm-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Litigations & Claims" },
  },
  "concluding-going-concern": {
    component: GoingConcernConcludingLanding,
    requiresSetActiveSection: true,
  },
  "concluding-compliance-laws-regulations": {
    component: ComplianceWithLawsAndRegulationsConcludingLanding,
    requiresSetActiveSection: true,
  },
  "concluding-review-estimates-bias": {
    component: ReviewOfEstimatesForManagementBiasConcludingLanding,
    requiresSetActiveSection: true,
  },
  "concluding-internal-audit": {
    component: InternalAuditConcludingLanding,
    requiresSetActiveSection: true,
  },
  "concluding-evaluate-audit-results": {
    component: EvaluateAuditResultsLanding,
    requiresSetActiveSection: true,
  },
  "payroll-tailoring": {
    component: TailoringQuestionsPanel,
  },
  "payroll-controls": {
    component: ControlsPanel,
  },
  "payroll-substantive-ipe_testing": {
    component: IPETesting,
  },
  "payroll-substantive-exception_testing": {
    component: ExceptionTesting,
  },
  "payroll-substantive-headcount_reconciliation": {
    component: HeadcountReconciliation,
  },
  "payroll-substantive-mom_analysis": {
    component: MoMAnalysis,
  },
  "payroll-substantive-increment_analysis": {
    component: IncrementAnalysis,
  },
  "payroll-substantive-salary_analytical": {
    component: SalaryAnalytical,
  },
  "payroll-substantive-actuary_testing": {
    component: ActuaryTesting,
  },
  "payroll-substantive-managerial_remuneration": {
    component: ManagerialRemuneration,
  },
  "payroll-substantive-share_based_payment": {
    component: ShareBasedPayment,
  },
  "payroll-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Employee Benefits Expense" },
  },
  "payroll-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Employee Benefits Expense" },
  },
  "ppe-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Property, Plant & Equipment" },
  },
  "ppe-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Property, Plant & Equipment" },
  },
  "ia-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Intangible Assets" },
  },
  "ia-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Intangible Assets" },
  },
  "trdpbl-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Trade Payables" },
  },
  "trdpbl-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Trade Payables" },
  },
  "trdrcv-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Trade Receivables" },
  },
  "trdrcv-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Trade Receivables" },
  },
  "depr-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Depreciation" },
  },
  "depr-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Depreciation" },
  },
  "othas-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Assets" },
  },
  "othas-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Assets" },
  },
  "ofa-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Financial Assets" },
  },
  "ofa-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Financial Assets" },
  },
  "othinc-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Income" },
  },
  "othinc-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Income" },
  },
  "rfo-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Revenue from Operations" },
  },
  "rfo-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Revenue from Operations" },
  },
  "inv-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Inventory" },
  },
  "inv-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Inventory" },
  },
  "cce-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Cash & Cash Equivalents" },
  },
  "cce-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Cash & Cash Equivalents" },
  },
  "fincst-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Finance Costs" },
  },
  "fincst-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Finance Costs" },
  },
  "inctx-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Income Tax" },
  },
  "inctx-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Income Tax" },
  },
  "invsmt-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Investments" },
  },
  "invsmt-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Investments" },
  },
  "pcl-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Provisions and Contingent Liabilities" },
  },
  "pcl-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Provisions and Contingent Liabilities" },
  },
  "llas-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Lease Liability" },
  },
  "llas-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Lease Liability" },
  },
  "ll-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Lease Liability - Ind AS" },
  },
  "ll-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Lease Liability - Ind AS" },
  },
  "rou-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Right-of-Use Assets" },
  },
  "rou-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Right-of-Use Assets" },
  },
  "imp-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Impairment Testing" },
  },
  "imp-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Impairment Testing" },
  },
  "othexp-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Expenses" },
  },
  "othexp-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Expenses" },
  },
  "othlib-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Liabilities" },
  },
  "othlib-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Liabilities" },
  },
  "ofllib-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Financial Liabilities" },
  },
  "ofllib-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Financial Liabilities" },
  },
  "othrec-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Other Receivables" },
  },
  "othrec-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Other Receivables" },
  },
  "golib-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Going Concern" },
  },
  "golib-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Going Concern" },
  },
  "litclm-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Litigations & Claims" },
  },
  "litclm-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Litigations & Claims" },
  },
  "opnbal-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Opening Balance Testing" },
  },
  "opnbal-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Opening Balance Testing" },
  },
  "relpard-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Related Party Disclosure" },
  },
  "relpard-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Related Party Disclosure" },
  },
  "eqt-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Equity" },
  },
  "eqt-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Equity" },
  },
  "mgtexp-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Management Use of Expert" },
  },
  "mgtexp-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Management Use of Expert" },
  },
  "useadtr-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Use of Auditor's Specialist" },
  },
  "useadtr-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Use of Auditor's Specialist" },
  },
  "cmplr-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Compliance with Laws and Regulations" },
  },
  "cmplr-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Compliance with Laws and Regulations" },
  },
  "rvestmb-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Review of Estimates for Management Bias" },
  },
  "rvestmb-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Review of Estimates for Management Bias" },
  },
  "intadt-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Internal Audit" },
  },
  "fcrp-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Financial Closing & Reporting Process" },
  },
  "moc-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Management Override of Controls" },
  },
  "fcrp-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Financial Closing & Reporting Process" },
  },
  "moc-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Management Override of Controls" },
  },
  "intadt-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Internal Audit" },
  },
  "comc-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Cost of Materials Consumed" },
  },
  "comc-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Cost of Materials Consumed" },
  },
  "bor-deficiencies": {
    component: WorkspaceDeficiencies,
    props: { workspaceName: "Borrowings" },
  },
  "bor-misstatements": {
    component: WorkspaceMisstatements,
    props: { workspaceName: "Borrowings" },
  },
  "ppe-tailoring": {
    component: PPETailoringQuestions,
  },
  "ppe-romms": {
    component: PPERomms,
  },
  "ppe-controls": {
    component: PPEInternalControls,
  },
  "ppe-substantive": {
    component: PPESubstantiveProcedures,
  },
  "ppe-ipe-testing": {
    component: PPEIPETesting,
  },
  "ppe-exception-testing": {
    component: PPEExceptionTesting,
  },
  "ppe-cwip-analysis": {
    component: PPECwipAnalysis,
  },
  "ppe-additions-deletions": {
    component: PPEAdditionsDeletions,
    props: { workspaceName: "Property, Plant & Equipment", procedureName: "Additions & Deletions" },
  },
  "ppe-non-current-assets-held-for-sale": {
    component: PPENonCurrentAssetsHeldForSale,
  },
  // Biological Assets other than Bearer Plants routes
  "bapb-tailoring": {
    component: BAPBTailoringQuestions,
  },
  "bapb-romms": {
    component: BAPBRomms,
  },
  "bapb-controls": {
    component: BAPBInternalControls,
  },
  "bapb-substantive": {
    component: BAPBSubstantiveProcedures,
  },
  "bapb-ipe-testing": {
    component: BAPBIPETesting,
  },
  "bapb-valuation-testing": {
    component: BAPBValuationTesting,
  },
  "bapb-physical-verification": {
    component: BAPBPhysicalVerification,
  },
  "bapb-disclosure-testing": {
    component: BAPBDisclosureTesting,
  },
  // Intangible Assets reuse PPE components for substantive procedures
  "ia-tailoring": {
    component: IATailoringQuestions,
  },
  "ia-ipe-testing": {
    component: IAIPETesting,
  },
  "ia-exception-testing": {
    component: IAExceptionTesting,
  },
  "ia-additions-deletions": {
    component: IAAdditionsDeletions,
    props: { workspaceName: "Intangible Assets", procedureName: "Additions & Deletions" },
  },
  "ia-under-development": {
    component: IAUnderDevelopment,
  },
  "ia-romms": {
    component: IARomms,
  },
  "ia-controls": {
    component: IntangibleAssetsInternalControls,
  },
  "depr-tailoring": { component: DepreciationTailoringQuestions },
  "depr-romms": { component: DepreciationRomms },
  "depr-controls": { component: DepreciationInternalControls },
  // Trade Payables routes
  "trdpbl-tailoring": { component: TradePayablesTailoringQuestions },
  "trdpbl-romms": { component: TradePayablesRomms },
  "trdpbl-controls": { component: TradePayablesInternalControls },
  "trdpbl-substantive": { component: TradePayablesSubstantiveProcedures },
  "trdpbl-ipe-testing": { component: TradePayableIPETesting },
  "trdpbl-exception-testing": { component: TradePayableExceptionTesting },
  "trdpbl-ratio-analysis": { component: TrdPblRatioAnalysis },
  // Other Assets routes
  "othas-tailoring": { component: OtherAssetsTailoringQuestions },
  "othas-romms": { component: OtherAssetsRomms },
  "othas-controls": { component: OtherAssetsInternalControls },
  "othas-substantive": { component: OtherAssetsSubstantive },
  "othas-ipe-testing": { component: OtherAssetsIPETesting },
  "othas-prepaid": { component: OtherAssetsPrepaidExpense },
  "othas-adv-suppliers-completeness": {
    component: AdvanceToSuppliersCompleteness,
    props: { variant: "other-assets-ind-as" },
  },
  "othas-capital-advances-completeness": {
    component: CapitalAdvancesCompleteness,
    props: { variant: "other-assets-ind-as" },
  },
  // Other Financial Assets routes
  "ofa-tailoring": { component: OtherFinancialAssetsTailoringQuestions },
  "ofa-romms": { component: OtherFinancialAssetsRomms },
  "ofa-controls": { component: OtherFinancialAssetsInternalControls },
  "ofa-substantive": { component: OtherFinancialAssetsSubstantive },
  "ofa-security-deposits": { component: OtherFinancialAssetsSecurityDeposits },
  // Other Income routes
  "othinc-tailoring": { component: OtherIncomeTailoringQuestions },
  "othinc-romms": { component: OtherIncomeRomms },
  "othinc-controls": { component: OtherIncomeInternalControls },
  "othinc-substantive": { component: OtherIncomeSubstantive },
  "othinc-net-investment": { component: OtherIncomeNetInvestmentOperatingLease },
  "rfo-tailoring": { component: RevenueFromOperationsTailoringQuestions },
  "rfo-romms": { component: RevenueFromOperationsRomms },
  "rfo-controls": { component: WorkspaceInternalControls, props: { workspaceName: "Revenue from Operations" } },
  "rfo-substantive": { component: RevenueFromOperationsSubstantive },
  "rfo-substantive-ship-bill-ipe-testing": { component: RFOShipAndBillIPETesting },
  "rfo-substantive-cost-plus-computation": { component: RFOCostPlusComputation },
  "depr-substantive": { component: DepreciationSubstantive },
  "depr-depreciation-rou": { component: DepreciationROUSubstantive },
  "depr-depreciation-tangible": { component: DepreciationTangibleAssets },
  "depr-depreciation-intangible": { component: DepreciationIntangibleAssets },
  "inv-tailoring": { component: InvTailoringQuestions },
  "inv-romms": { component: InvRomms },
  "inv-controls": { component: InvInternalControls },
  "inv-substantive": { component: InvSubstantive },
  // Inventory substantive sub-modules
  "inv-ipe-testing": { component: InventoryExecutionIPETesting },
  "inv-exception-testing": { component: InvExceptionTesting },
  "inv-nrv-assessment": { component: InvNRVAssessment, props: { workspaceName: "Inventory", procedureName: "NRV Assessment" } },
  "inv-cost-allocation": { component: InvCostAllocation },
  "inv-inventory-ageing": { component: InvAgeingAnalysis },
  "inv-physical-verification": { component: InvPhysicalVerification, props: { workspaceName: "Inventory", procedureName: "Physical Verification" } },
  "inv-overhead-working": { component: InvSubstantive },
  // CCE
  "cce-tailoring": { component: CCETailoringQuestions },
  "cce-romms": { component: CCERomms },
  "cce-controls": { component: CCEInternalControls },
  "cce-substantive": { component: CCESubstantiveProcedures },
  "cce-substantive-bank-accounts": {
    component: CCESubstantive,
    props: { procedureId: "bank-accounts" }
  },
  // Finance Cost
  "fincst-tailoring": { component: FinCstTailoringQuestions },
  "fincst-romms": { component: FinCstRomms },
  "fincst-controls": { component: FinCstInternalControls },
  "fincst-substantive": { component: FinCstSubstantive },
  // Income Tax
  "inctx-tailoring": { component: IncTxTailoringQuestions },
  "inctx-romms": { component: IncTxRomms },
  "inctx-controls": { component: IncTxInternalControls },
  "inctx-substantive": { component: IncTxSubstantiveProcedures },
  // Investments
  "invsmt-tailoring": { component: InvsmtTailoringQuestions },
  "invsmt-romms": { component: InvsmtRomms },
  "invsmt-controls": { component: InvsmtInternalControls },
  "invsmt-substantive": { component: InvsmtSubstantiveProcedures },
  "invsmt-substantive-investments-procedures": {
    component: InvsmtSubstantive,
    props: { procedureId: "investments-procedures" }
  },
  "pcl-tailoring": { component: PCLTailoringQuestions },
  "pcl-romms": { component: PCLRomms },
  "pcl-controls": { component: PCLInternalControls },
  "pcl-substantive": { component: PCLSubstantiveProcedures },
  "pcl-substantive-other-provisions": {
    component: PCLSubstantive,
    props: { procedureId: "other-provisions" }
  },
  "ll-as-tailoring": { component: LeaseLiabilityClassicTailoringQuestions },
  "ll-as-romms": { component: LeaseLiabilityClassicRomms },
  "ll-as-controls": { component: LeaseLiabilityClassicInternalControls },
  "ll-as-substantive": { component: LeaseLiabilityClassicSubstantive },
  "ll-as-lease-assessment": {
    component: LeaseAssessment,
  },
  "ll-as-finance-lease": {
    component: FinanceLease,
  },
  "ll-as-operating-lease": {
    component: OperatingLease,
  },
  "ll-tailoring": { component: LeaseLiabilityTailoringQuestions },
  "ll-romms": { component: LeaseLiabilityRomms },
  "ll-controls": { component: LeaseLiabilityInternalControls },
  "ll-substantive": { component: LeaseLiabilitySubstantive },
  "rou-tailoring": { component: RightOfUseAssetsTailoringQuestions },
  "rou-romms": { component: RightOfUseAssetsRomms },
  "rou-controls": { component: RightOfUseAssetsInternalControls },
  "rou-substantive": { component: RightOfUseAssetsSubstantive },
  "rou-disclosure": { component: RightOfUseDisclosureProcedure },
  "ll-lease-assessment": {
    component: LeaseAssessmentIndAS,
  },
  "ll-lease-testing": {
    component: LeaseTestingIndAS,
  },
  "ll-lease-reassessment": {
    component: LeaseReassessmentIndAS,
  },
  "ll-lease-modification": {
    component: LeaseModificationIndAS,
  },
  "ll-lease-accounting-exception": {
    component: LeaseAccountingExceptionIndAS,
  },
  "ll-lease-disclosure": {
    component: LeaseDisclosureIndAS,
  },
  "imp-tailoring": { component: ImpairmentTailoringQuestions },
  "imp-romms": { component: ImpairmentRomms },
  "imp-controls": { component: ImpairmentInternalControls },
  "imp-substantive": { component: ImpairmentSubstantive },
  "trdrcv-tailoring": { component: TrdRcvTailoringQuestions },
  "trdrcv-romms": { component: TrdRcvRomms },
  "trdrcv-controls": { component: TrdRcvInternalControls },
  "trdrcv-substantive": { component: TrdRcvSubstantive },
  "trdrcv-ipe-testing": { component: TrdRcvIPETesting },
  "trdrcv-exception-testing": { component: TrdRcvExceptionTesting },
  "trdrcv-ratio-analysis": { component: TrdRcvRatioAnalysis },
  "trdrcv-ecl-flow-rate-yoy": { component: TrdRcvEclFlowRateYoy },
  "trdrcv-ecl-flow-rate-qoq": { component: TrdRcvEclFlowRateQoq },
  "othexp-tailoring": { component: OthExpTailoringQuestions },
  "othexp-romms": { component: OthExpRomms },
  "othexp-controls": { component: OthExpInternalControls },
  "othexp-substantive": { component: OthExpSubstantiveProcedures },
  "othexp-substantive-ipe-testing": { component: OthExpSubstantive, props: { procedureId: "ipe-testing" } },
  "othexp-substantive-analytical-procedures": { component: OthExpSubstantive, props: { procedureId: "analytical-procedures" } },
  "othexp-substantive-corporate-social-responsibility": { component: OthExpSubstantive, props: { procedureId: "corporate-social-responsibility" } },
  // Other Liabilities routes
  "othlib-tailoring": { component: OtherLiabilitiesTailoringQuestions },
  "othlib-romms": { component: OtherLiabilitiesRomms },
  "othlib-controls": { component: OtherLiabilitiesInternalControls },
  "othlib-substantive": { component: OtherLiabilitiesSubstantive },
  "othlib-adv-cust-completeness": { component: AdvanceFromCustomerCompleteness },
  "othlib-adv-cust-ageing": {
    component: AgeingAnalysisModule,
    props: {
      area: "Execution",
      section: "Other Liabilities",
      workspaceName: "Other Liabilities",
      workspacePrefix: "OTL",
    },
  },
  "othlib-capex-vendors-completeness": { component: CapexVendorsCompleteness },
  "othlib-capex-vendors-ageing": {
    component: AgeingAnalysisModule,
    props: {
      area: "Execution",
      section: "Other Liabilities",
      workspaceName: "Other Liabilities",
      workspacePrefix: "OTL",
    },
  },
  "ofllib-tailoring": { component: OtherFinancialLiabilitiesTailoringQuestions },
  "ofllib-romms": { component: OtherFinancialLiabilitiesRomms },
  "ofllib-controls": { component: OtherFinancialLiabilitiesInternalControls },
  "ofllib-substantive": { component: OtherFinancialLiabilitiesSubstantive },
  "ofllib-capex-vendors-completeness": { component: CapexVendorsCompletenessOFL },
  // Other Receivables routes
  "othrec-tailoring": { component: OtherReceivablesTailoringQuestions },
  "othrec-romms": { component: OtherReceivablesRomms },
  "othrec-controls": { component: OtherReceivablesInternalControls },
  "othrec-substantive": { component: OtherReceivablesSubstantive },
  "othrec-adv-suppliers-completeness": { component: AdvanceToSuppliersCompleteness },
  "othrec-capital-advances-completeness": { component: CapitalAdvancesCompleteness },
  // Going Concern routes
  "golib-tailoring": { component: GoingConcernTailoringQuestions },
  "golib-romms": { component: GoingConcernRomms },
  "golib-controls": { component: GoingConcernInternalControls },
  "golib-substantive": { component: GoingConcernSubstantive },
  "golib-substantive-going-concern": { component: GoingConcernSubstantiveQuestions },
  // Litigations & Claims routes
  "litclm-tailoring": { component: LitigationsClaimsTailoringQuestions },
  "litclm-romms": { component: LitigationsClaimsRomms },
  "litclm-controls": { component: LitigationsClaimsInternalControls },
  "litclm-substantive": { component: LitigationsClaimsSubstantive },
  "litclm-substantive-litigations-claims": { component: LitigationsClaimsSubstantiveQuestions },
  "litclm-substantive-assessment": { component: LitigationsClaimsAssessment },
  // Opening Balance Testing routes
  "opnbal-tailoring": { component: OpeningBalanceTestingTailoringQuestions },
  "opnbal-romms": { component: OpeningBalanceTestingRomms },
  "opnbal-controls": { component: OpeningBalanceTestingInternalControls },
  "opnbal-substantive": { component: OpeningBalanceTestingSubstantiveProcedures },
  "opnbal-substantive-inquiry-predecessor": {
    component: OpeningBalanceTestingSubstantive,
    props: { procedureId: "inquiry-predecessor" }
  },
  "opnbal-substantive-sa510-assessment": {
    component: OpeningBalanceTestingSubstantive,
    props: { procedureId: "sa510-assessment" }
  },
  // Related Party Disclosure routes
  "relpard-tailoring": { component: RelatedPartyDisclosureTailoringQuestions },
  "relpard-romms": { component: RelatedPartyDisclosureRomms },
  "relpard-controls": { component: RelatedPartyDisclosureInternalControls },
  "relpard-substantive": { component: RelatedPartyDisclosureSubstantive },
  "relpard-compliance": { component: ComplianceSubstantive },
  "relpard-arms-length": { component: ArmsLengthAssessmentSubstantive },
  // Equity routes
  "eqt-tailoring": { component: EquityTailoringQuestions },
  "eqt-romms": { component: EquityRomms },
  "eqt-controls": { component: EquityInternalControls },
  "eqt-substantive": { component: EquitySubstantive },
  // Management Use of Expert routes
  "mgtexp-tailoring": { component: ManagementUseOfExpertTailoringQuestions },
  "mgtexp-romms": { component: ManagementUseOfExpertRomms },
  "mgtexp-controls": { component: ManagementUseOfExpertInternalControls },
  "mgtexp-substantive": { component: ManagementUseOfExpertSubstantive },
  "mgtexp-assessment": { component: ManagementUseOfExpertAssessment },
  // Use of Auditor's Specialist routes
  "useadtr-tailoring": { component: UseOfAuditorsSpecialistTailoringQuestions },
  "useadtr-romms": { component: UseOfAuditorsSpecialistRomms },
  "useadtr-controls": { component: UseOfAuditorsSpecialistInternalControls },
  "useadtr-substantive": { component: UseOfAuditorsSpecialistSubstantive },
  // Compliance with laws and regulations routes
  "cmplr-tailoring": { component: ComplianceWithLawsAndRegulationsTailoringQuestions },
  "cmplr-romms": { component: ComplianceWithLawsAndRegulationsRomms },
  "cmplr-controls": { component: ComplianceWithLawsAndRegulationsInternalControls },
  "cmplr-substantive": { component: ComplianceWithLawsAndRegulationsSubstantive },
  // Review of estimates for management bias routes
  "rvestmb-tailoring": { component: ReviewOfEstimatesForManagementBiasTailoringQuestions },
  "rvestmb-romms": { component: ReviewOfEstimatesForManagementBiasRomms },
  "rvestmb-controls": { component: ReviewOfEstimatesForManagementBiasInternalControls },
  "rvestmb-substantive": { component: ReviewOfEstimatesForManagementBiasSubstantive },
  // Internal Audit routes
  "intadt-tailoring": { component: InternalAuditTailoringQuestions },
  "intadt-romms": { component: InternalAuditRomms },
  "intadt-controls": { component: InternalAuditInternalControls },
  "intadt-substantive": { component: InternalAuditSubstantive },
  "fcrp-tailoring": { component: FCRPTailoringQuestions },
  "fcrp-romms": { component: FCRPRomms },
  "fcrp-controls": { component: FCRPInternalControls },
  "fcrp-substantive": { component: FCRPSubstantive },
  "moc-tailoring": { component: MOCTailoringQuestions },
  "moc-romms": { component: MOCRomms },
  "moc-controls": { component: MOCInternalControls },
  "moc-substantive": { component: MOCSubstantive },
  "comc-tailoring": { component: COMCTailoringQuestions },
  "comc-romms": { component: COMCRomms },
  "comc-controls": { component: COMCInternalControls },
  "comc-substantive": { component: COMCSubstantiveProcedures },
  "comc-substantive-ipe-testing": { component: COMCSubstantive, props: { procedureId: "ipe-testing" } },
  "comc-substantive-exception-testing": { component: COMCSubstantive, props: { procedureId: "exception-testing" } },
  "comc-substantive-test-of-details": { component: COMCSubstantive, props: { procedureId: "test-of-details" } },
  "comc-substantive-analytical-procedures": { component: COMCSubstantive, props: { procedureId: "analytical-procedures" } },
  "comc-substantive-predictive-analytics": { component: COMCSubstantive, props: { procedureId: "predictive-analytics" } },
  "comc-substantive-purchase-analytical-dashboard": { component: COMCPurchaseAnalyticalDashboard },
  "bor-tailoring": { component: BorrTailoringQuestions },
  "bor-romms": { component: BorrRomms },
  "bor-controls": { component: BorrInternalControls },
  "bor-substantive": { component: BorrSubstantiveProcedures },
  "bor-substantive-repayment-testing": { component: BorrSubstantive, props: { procedureId: "repayment-testing" } },
  "bor-substantive-covenant-testing": { component: BorrSubstantive, props: { procedureId: "covenant-testing" } },
  "bor-substantive-drawing-power": { component: BorrSubstantive, props: { procedureId: "drawing-power" } },
  "bor-substantive-agreement-summary": { component: BorrSubstantive, props: { procedureId: "agreement-summary" } },
  "bor-substantive-quarterly-stock-statement": { component: BorrSubstantive, props: { procedureId: "quarterly-stock-statement" } },
  "bor-substantive-classification-testing": { component: BorrSubstantive, props: { procedureId: "classification-testing" } },
  "borindas-tailoring": { component: BorrIndASTailoringQuestions },
  "borindas-romms": { component: BorrIndASRomms },
  "borindas-controls": { component: BorrIndASInternalControls },
  "borindas-substantive": { component: BorrIndASSubstantiveProcedures },
  "borindas-substantive-dashboard-agreement-summary": { component: BorrIndASSubstantive, props: { procedureId: "dashboard-agreement-summary" } },
  "borindas-substantive-repayment-testing": { component: BorrIndASSubstantive, props: { procedureId: "repayment-testing" } },
  "borindas-substantive-covenant-testing": { component: BorrIndASSubstantive, props: { procedureId: "covenant-testing" } },
  "borindas-substantive-drawing-power": { component: BorrIndASSubstantive, props: { procedureId: "drawing-power" } },
  "borindas-substantive-utilisation-testing": { component: BorrIndASSubstantive, props: { procedureId: "utilisation-testing" } },
  "borindas-substantive-quarterly-stock-statement": { component: BorrIndASSubstantive, props: { procedureId: "quarterly-stock-statement" } },
  "borindas-substantive-classification-testing": { component: BorrIndASSubstantive, props: { procedureId: "classification-testing" } },
  // Planning Landing Pages
  "planning-engagement-acceptance": {
    component: EngagementAcceptanceLanding,
    requiresSetActiveSection: true,
  },
  "planning-understanding-entity": {
    component: UnderstandingEntityLanding,
    requiresSetActiveSection: true,
  },
  "planning-materiality": {
    component: MaterialitySection,
  },
  "planning-preliminary-analytical": {
    component: PreliminaryAnalyticsSection,
  },
  "planning-fraud-risk": {
    component: FraudRiskLanding,
    requiresSetActiveSection: true,
  },
  "planning-it-risk": {
    component: ITRiskLanding,
    requiresSetActiveSection: true,
  },
  "planning-independence": {
    component: IndependenceLanding,
    requiresSetActiveSection: true,
  },
  // Engagement Acceptance Sub-sections
  "engagement-acceptance-engagement-profiling": {
    component: PlanningSection,
    props: { sectionKey: "engagement-acceptance", title: "Engagement Profiling", subSection: "Engagement Profiling" },
  },
  "engagement-acceptance-compliance-ethical-requirements": {
    component: PlanningSection,
    props: { sectionKey: "engagement-acceptance", title: "Compliance with Ethical Requirements", subSection: "Compliance with Ethical Requirements" },
  },
  "engagement-acceptance-scope-of-review": {
    component: PlanningSection,
    props: { sectionKey: "engagement-acceptance", title: "Scope of Review", subSection: "Scope of Review" },
  },
  "engagement-acceptance-assess-engagement-risk": {
    component: PlanningSection,
    props: { sectionKey: "engagement-acceptance", title: "Assess Engagement Risk", subSection: "Assess Engagement Risk" },
  },
  "engagement-acceptance-agreeing-terms-audit-engagement": {
    component: PlanningSection,
    props: { sectionKey: "engagement-acceptance", title: "Agreeing the Terms of Audit Engagement", subSection: "Agreeing the Terms of Audit Engagement" },
  },
  // Understanding Entity Sub-sections
  "understanding-entity-external-factors": {
    component: PlanningSection,
    props: { sectionKey: "understanding-entity", title: "External Factors", subSection: "External Factors" },
  },
  "understanding-entity-internal-factors": {
    component: PlanningSection,
    props: { sectionKey: "understanding-entity", title: "Internal Factors", subSection: "Internal Factors" },
  },
  "understanding-entity-accounting-policies": {
    component: PlanningSection,
    props: { sectionKey: "understanding-entity", title: "Accounting Policies", subSection: "Accounting Policies" },
  },
  "understanding-entity-other-considerations": {
    component: PlanningSection,
    props: { sectionKey: "understanding-entity", title: "Other Considerations", subSection: "Other Considerations" },
  },
  "understanding-entity-understanding-internal-controls": {
    component: PlanningSection,
    props: { sectionKey: "understanding-entity", title: "Understanding Internal Controls", subSection: "Understanding Internal Controls" },
  },
  // Materiality Sub-sections
  "materiality-overall-materiality": {
    component: PlanningSection,
    props: { sectionKey: "materiality", title: "Overall Materiality", subSection: "Overall Materiality" },
  },
  "materiality-performance-materiality": {
    component: PlanningSection,
    props: { sectionKey: "materiality", title: "Performance Materiality", subSection: "Performance Materiality" },
  },
  "materiality-component-materiality": {
    component: PlanningSection,
    props: { sectionKey: "materiality", title: "Component Materiality", subSection: "Component Materiality" },
  },
  "materiality-documentation": {
    component: PlanningSection,
    props: { sectionKey: "materiality", title: "Documentation", subSection: "Documentation" },
  },
  "materiality-revision": {
    component: PlanningSection,
    props: { sectionKey: "materiality", title: "Materiality Revision", subSection: "Materiality Revision" },
  },
  "materiality-approval": {
    component: PlanningSection,
    props: { sectionKey: "materiality", title: "Approval", subSection: "Approval" },
  },
  // Preliminary Analytical Sub-sections
  "preliminary-analytical-ratio-analysis": {
    component: PlanningSection,
    props: { sectionKey: "preliminary-analytical", title: "Ratio Analysis", subSection: "Ratio Analysis" },
  },
  "preliminary-analytical-trend-analysis": {
    component: PlanningSection,
    props: { sectionKey: "preliminary-analytical", title: "Trend Analysis", subSection: "Trend Analysis" },
  },
  "preliminary-analytical-benchmarking": {
    component: PlanningSection,
    props: { sectionKey: "preliminary-analytical", title: "Benchmarking", subSection: "Benchmarking" },
  },
  "preliminary-analytical-documentation": {
    component: PlanningSection,
    props: { sectionKey: "preliminary-analytical", title: "Documentation", subSection: "Documentation" },
  },
  "preliminary-analytical-risk-assessment": {
    component: PlanningSection,
    props: { sectionKey: "preliminary-analytical", title: "Risk Assessment", subSection: "Risk Assessment" },
  },
  "preliminary-analytical-approval": {
    component: PlanningSection,
    props: { sectionKey: "preliminary-analytical", title: "Approval", subSection: "Approval" },
  },
  // Fraud Risk Sub-sections
  "fraud-risk-inquiries-management": {
    component: PlanningSection,
    props: { sectionKey: "fraud-risk", title: "Inquiries of the management and others within the entity", subSection: "Inquiries of the management and others within the entity" },
  },
  "fraud-risk-planning-discussion": {
    component: PlanningSection,
    props: { sectionKey: "fraud-risk", title: "Planning Discussion with the Client/Entity", subSection: "Planning Discussion with the Client/Entity" },
  },
  "fraud-risk-fraud-risk-factors": {
    component: PlanningSection,
    props: { sectionKey: "fraud-risk", title: "Fraud Risk Factors", subSection: "Fraud Risk Factors" },
  },
  "fraud-risk-management-override": {
    component: PlanningSection,
    props: { sectionKey: "fraud-risk", title: "Management Override of Controls", subSection: "Management Override of Controls" },
  },
  "fraud-risk-communication-management": {
    component: PlanningSection,
    props: { sectionKey: "fraud-risk", title: "Communication with management and those charged with governance regarding matters of fraud", subSection: "Communication with management and those charged with governance regarding matters of fraud" },
  },
  // IT Risk Sub-sections
  "it-risk-it-risk-assessment": {
    component: PlanningSection,
    props: { sectionKey: "it-risk", title: "IT Risk Assessment", subSection: "IT Risk Assessment" },
  },
};

/**
 * Special routes that require custom rendering logic
 */
export const specialRoutes = {
  "planning-overview": "planning-overview",
  "payroll-romms": "payroll-romms",
  "payroll-substantive": "payroll-substantive",
  "payroll-deficiencies": "payroll-deficiencies",
  "payroll-misstatements": "payroll-misstatements",
  "ppe-deficiencies": "ppe-deficiencies",
  "ppe-misstatements": "ppe-misstatements",
  "ia-deficiencies": "ia-deficiencies",
  "ia-misstatements": "ia-misstatements",
  "trdpbl-deficiencies": "trdpbl-deficiencies",
  "trdpbl-misstatements": "trdpbl-misstatements",
  "trdrcv-deficiencies": "trdrcv-deficiencies",
  "trdrcv-misstatements": "trdrcv-misstatements",
  "depr-deficiencies": "depr-deficiencies",
  "depr-misstatements": "depr-misstatements",
  "othas-deficiencies": "othas-deficiencies",
  "othas-misstatements": "othas-misstatements",
  "ofa-deficiencies": "ofa-deficiencies",
  "ofa-misstatements": "ofa-misstatements",
  "othinc-deficiencies": "othinc-deficiencies",
  "othinc-misstatements": "othinc-misstatements",
  "inv-deficiencies": "inv-deficiencies",
  "inv-misstatements": "inv-misstatements",
  "cce-deficiencies": "cce-deficiencies",
  "cce-misstatements": "cce-misstatements",
  "fincst-deficiencies": "fincst-deficiencies",
  "fincst-misstatements": "fincst-misstatements",
  "inctx-deficiencies": "inctx-deficiencies",
  "inctx-misstatements": "inctx-misstatements",
  "invsmt-deficiencies": "invsmt-deficiencies",
  "invsmt-misstatements": "invsmt-misstatements",
  "pcl-deficiencies": "pcl-deficiencies",
  "pcl-misstatements": "pcl-misstatements",
  "llas-deficiencies": "llas-deficiencies",
  "llas-misstatements": "llas-misstatements",
  "ll-deficiencies": "ll-deficiencies",
  "ll-misstatements": "ll-misstatements",
  "rou-deficiencies": "rou-deficiencies",
  "rou-misstatements": "rou-misstatements",
  "imp-deficiencies": "imp-deficiencies",
  "imp-misstatements": "imp-misstatements",
  "othexp-deficiencies": "othexp-deficiencies",
  "othexp-misstatements": "othexp-misstatements",
  "othlib-deficiencies": "othlib-deficiencies",
  "othlib-misstatements": "othlib-misstatements",
  "ofllib-deficiencies": "ofllib-deficiencies",
  "ofllib-misstatements": "ofllib-misstatements",
  "othrec-deficiencies": "othrec-deficiencies",
  "othrec-misstatements": "othrec-misstatements",
  "golib-deficiencies": "golib-deficiencies",
  "golib-misstatements": "golib-misstatements",
  "litclm-deficiencies": "litclm-deficiencies",
  "litclm-misstatements": "litclm-misstatements",
  "opnbal-deficiencies": "opnbal-deficiencies",
  "opnbal-misstatements": "opnbal-misstatements",
  "relpard-deficiencies": "relpard-deficiencies",
  "relpard-misstatements": "relpard-misstatements",
  "eqt-deficiencies": "eqt-deficiencies",
  "eqt-misstatements": "eqt-misstatements",
  "mgtexp-deficiencies": "mgtexp-deficiencies",
  "mgtexp-misstatements": "mgtexp-misstatements",
  "useadtr-deficiencies": "useadtr-deficiencies",
  "useadtr-misstatements": "useadtr-misstatements",
  "cmplr-deficiencies": "cmplr-deficiencies",
  "cmplr-misstatements": "cmplr-misstatements",
  "rvestmb-deficiencies": "rvestmb-deficiencies",
  "rvestmb-misstatements": "rvestmb-misstatements",
  "intadt-deficiencies": "intadt-deficiencies",
  "intadt-misstatements": "intadt-misstatements",
  "fcrp-deficiencies": "fcrp-deficiencies",
  "fcrp-misstatements": "fcrp-misstatements",
  "moc-deficiencies": "moc-deficiencies",
  "moc-misstatements": "moc-misstatements",
  "comc-deficiencies": "comc-deficiencies",
  "comc-misstatements": "comc-misstatements",
  "bor-deficiencies": "bor-deficiencies",
  "bor-misstatements": "bor-misstatements",
  "ppe-romms": "ppe-romms",
  "ppe-substantive": "ppe-substantive",
  "ia-substantive": "ia-substantive",
  "comc-substantive": "comc-substantive",
  "execution-trade-payables": "execution-trade-payables",
  planning: "planning",
  "engagement-acceptance": "planning",
  "fraud-risk": "planning",
  "it-risk": "planning",
  materiality: "planning",
  "preliminary-analytical": "planning",
  "understanding-entity": "planning",
  // New planning landing pages
  "planning-engagement-acceptance": "planning-engagement-acceptance",
  "planning-understanding-entity": "planning-understanding-entity",
  "planning-materiality": "planning-materiality",
  "planning-preliminary-analytical": "planning-preliminary-analytical",
  "planning-fraud-risk": "planning-fraud-risk",
  "planning-it-risk": "planning-it-risk",
  "planning-independence": "planning-independence",
  // Engagement Acceptance Sub-sections
  "engagement-acceptance-engagement-profiling": "engagement-acceptance-engagement-profiling",
  "engagement-acceptance-compliance-ethical-requirements": "engagement-acceptance-compliance-ethical-requirements",
  "engagement-acceptance-scope-of-review": "engagement-acceptance-scope-of-review",
  "engagement-acceptance-assess-engagement-risk": "engagement-acceptance-assess-engagement-risk",
  "engagement-acceptance-agreeing-terms-audit-engagement": "engagement-acceptance-agreeing-terms-audit-engagement",
  // Understanding Entity Sub-sections
  "understanding-entity-external-factors": "understanding-entity-external-factors",
  "understanding-entity-internal-factors": "understanding-entity-internal-factors",
  "understanding-entity-accounting-policies": "understanding-entity-accounting-policies",
  "understanding-entity-other-considerations": "understanding-entity-other-considerations",
  "understanding-entity-understanding-internal-controls": "understanding-entity-understanding-internal-controls",
  // Materiality Sub-sections
  "materiality-overall-materiality": "materiality-overall-materiality",
  "materiality-performance-materiality": "materiality-performance-materiality",
  "materiality-component-materiality": "materiality-component-materiality",
  "materiality-documentation": "materiality-documentation",
  "materiality-revision": "materiality-revision",
  "materiality-approval": "materiality-approval",
  // Preliminary Analytical Sub-sections
  "preliminary-analytical-ratio-analysis": "preliminary-analytical-ratio-analysis",
  "preliminary-analytical-trend-analysis": "preliminary-analytical-trend-analysis",
  "preliminary-analytical-benchmarking": "preliminary-analytical-benchmarking",
  "preliminary-analytical-documentation": "preliminary-analytical-documentation",
  "preliminary-analytical-risk-assessment": "preliminary-analytical-risk-assessment",
  "preliminary-analytical-approval": "preliminary-analytical-approval",
  // Fraud Risk Sub-sections
  "fraud-risk-inquiries-management": "fraud-risk-inquiries-management",
  "fraud-risk-planning-discussion": "fraud-risk-planning-discussion",
  "fraud-risk-fraud-risk-factors": "fraud-risk-fraud-risk-factors",
  "fraud-risk-management-override": "fraud-risk-management-override",
  "fraud-risk-communication-management": "fraud-risk-communication-management",
  // IT Risk Sub-sections
  "it-risk-it-risk-assessment": "it-risk-it-risk-assessment",
};

/**
 * Renders special route components that require custom logic
 */
export function renderSpecialRoute(
  activeSection: string,
  searchTerm: string,
  setActiveSection?: (section: string) => void
): React.ReactNode {
  switch (activeSection) {
    case "payroll-romms":
      return (
        <PayrollRomms
          onComplete={(data: any) => {
            logger.dataSave("ROMMS", data);
            setActiveSection?.("execution-payroll");
          }}
        />
      );

    case "payroll-substantive":
      return (
        <SubstantiveProcedures
          setActiveSection={setActiveSection}
        />
      );

    case "payroll-deficiencies":
      return (
        <WorkspaceDeficiencies
          workspaceName="Employee Benefits Expense"
        />
      );

    case "payroll-misstatements":
      return (
        <WorkspaceMisstatements
          workspaceName="Employee Benefits Expense"
        />
      );

    case "llas-misstatements":
      return (
        <WorkspaceMisstatements
          workspaceName="Lease Liability"
        />
      );

    case "ppe-deficiencies":
      return (
        <WorkspaceDeficiencies
          workspaceName="Property, Plant & Equipment"
        />
      );

    case "ppe-misstatements":
      return (
        <WorkspaceMisstatements
          workspaceName="Property, Plant & Equipment"
        />
      );

    case "payroll-substantive-ipe_testing":
      return (
        <IPETesting
        />
      );

    case "payroll-substantive-exception_testing":
      return (
        <ExceptionTesting
        />
      );

    case "payroll-substantive-headcount_reconciliation":
      return (
        <HeadcountReconciliation
        />
      );

    case "payroll-substantive-mom_analysis":
      return (
        <MoMAnalysis
        />
      );

    case "payroll-substantive-increment_analysis":
      return (
        <IncrementAnalysis
        />
      );

    case "payroll-substantive-salary_analytical":
      return (
        <SalaryAnalytical
        />
      );

    case "payroll-substantive-actuary_testing":
      return (
        <ActuaryTesting
        />
      );

    case "payroll-substantive-managerial_remuneration":
      return (
        <ManagerialRemuneration
        />
      );

    case "payroll-substantive-share_based_payment":
      return (
        <ShareBasedPayment
        />
      );

    case "ppe-romms":
      return (
        <PPERomms
        />
      );

    case "ppe-substantive":
      return (
        <PPESubstantiveProcedures
          setActiveSection={setActiveSection}
        />
      );

    case "bapb-romms":
      return (
        <BAPBRomms
        />
      );

    case "bapb-substantive":
      return (
        <BAPBSubstantiveProcedures
          setActiveSection={setActiveSection}
        />
      );

    case "ppe-cwip-analysis":
      return (
        <PPECwipAnalysis
          setActiveSection={setActiveSection}
        />
      );

    case "ppe-ipe-testing":
      return (
        <PPEIPETesting
          setActiveSection={setActiveSection}
        />
      );

    case "ppe-exception-testing":
      return (
        <PPEExceptionTesting
          setActiveSection={setActiveSection}
        />
      );

    case "ppe-additions-deletions":
      return (
        <PPEAdditionsDeletions
          setActiveSection={setActiveSection}
        />
      );

    case "ia-substantive":
      return (
        <IASubstantiveProcedures
          setActiveSection={setActiveSection}
        />
      );

    case "execution-payroll":
      if (!setActiveSection) return <PayrollRunner />;
      return <PayrollLanding onSelect={setActiveSection} />;

    case "execution-ppe":
      if (!setActiveSection) return <PPELanding onSelect={() => { }} />;
      return <PPELanding onSelect={setActiveSection} />;

    case "execution-bapb":
      if (!setActiveSection) return <BAPBLanding onSelect={() => { }} />;
      return <BAPBLanding onSelect={setActiveSection} />;

    case "execution-ia":
      if (!setActiveSection) return <IntangibleAssetsLanding onSelect={() => { }} />;
      return <IntangibleAssetsLanding onSelect={setActiveSection} />;

    case "execution-depreciation":
      if (!setActiveSection) return <DepreciationLanding onSelect={() => { }} />;
      return <DepreciationLanding onSelect={setActiveSection} />;

    case "execution-trade-payables":
      if (!setActiveSection) return <TradePayablesLanding onSelect={() => { }} />;
      return <TradePayablesLanding onSelect={setActiveSection} />;

    case "execution-other-assets":
      if (!setActiveSection) return <OtherAssetsLanding onSelect={() => { }} />;
      return <OtherAssetsLanding onSelect={setActiveSection} />;

    case "execution-other-financial-assets":
      if (!setActiveSection) return <OtherFinancialAssetsLanding onSelect={() => { }} />;
      return <OtherFinancialAssetsLanding onSelect={setActiveSection} />;

    case "execution-lease-liability":
      if (!setActiveSection) return <LeaseLiabilityLanding onSelect={() => { }} />;
      return <LeaseLiabilityLanding onSelect={setActiveSection} />;

    case "execution-lease-liability-ind-as":
      if (!setActiveSection) return <LeaseLiabilityIndASLanding onSelect={() => { }} />;
      return <LeaseLiabilityIndASLanding onSelect={setActiveSection} />;

    case "execution-right-of-use-assets":
      if (!setActiveSection) return <RightOfUseAssetsLanding onSelect={() => { }} />;
      return <RightOfUseAssetsLanding onSelect={setActiveSection} />;

    case "ll-tailoring":
      return (
        <LeaseLiabilityTailoringQuestions
        />
      );

    case "ll-romms":
      return (
        <LeaseLiabilityRomms
        />
      );

    case "ll-controls":
      return (
        <LeaseLiabilityInternalControls
        />
      );

    case "ll-substantive":
      return (
        <LeaseLiabilitySubstantive
          setActiveSection={setActiveSection}
        />
      );

    case "ll-lease-assessment":
      return (
        <LeaseAssessmentIndAS
          setActiveSection={setActiveSection}
        />
      );

    case "ll-lease-testing":
      return (
        <LeaseTestingIndAS
          setActiveSection={setActiveSection}
        />
      );

    case "ll-lease-reassessment":
      return (
        <LeaseReassessmentIndAS
          setActiveSection={setActiveSection}
        />
      );

    case "ll-lease-modification":
      return (
        <LeaseModificationIndAS
          setActiveSection={setActiveSection}
        />
      );

    case "ll-lease-accounting-exception":
      return (
        <LeaseAccountingExceptionIndAS
          setActiveSection={setActiveSection}
        />
      );

    case "ll-lease-disclosure":
      return (
        <LeaseDisclosureIndAS
          setActiveSection={setActiveSection}
        />
      );

    case "rou-tailoring":
      return (
        <RightOfUseAssetsTailoringQuestions
        />
      );

    case "rou-romms":
      return (
        <RightOfUseAssetsRomms
        />
      );

    case "rou-controls":
      return (
        <RightOfUseAssetsInternalControls
        />
      );

    case "rou-substantive":
      return (
        <RightOfUseAssetsSubstantive
          setActiveSection={setActiveSection}
        />
      );

    case "rou-disclosure":
      return (
        <RightOfUseDisclosureProcedure setActiveSection={setActiveSection} />
      );

    case "execution-impairment":
      if (!setActiveSection) return <ImpairmentLanding onSelect={() => { }} />;
      return <ImpairmentLanding onSelect={setActiveSection} />;

    case "execution-trade-receivables":
      if (!setActiveSection) return <TradeReceivablesLanding onSelect={() => { }} />;
      return <TradeReceivablesLanding onSelect={setActiveSection} />;

    case "execution-other-expenses":
      if (!setActiveSection) return <OtherExpensesLanding onSelect={() => { }} />;
      return <OtherExpensesLanding onSelect={setActiveSection} />;

    case "execution-borrowings":
      if (!setActiveSection) return <BorrowingsLanding onSelect={() => { }} />;
      return <BorrowingsLanding onSelect={setActiveSection} />;

    case "execution-comc":
      if (!setActiveSection) return <COMCLanding onSelect={() => { }} />;
      return <COMCLanding onSelect={setActiveSection} />;

    case "planning-overview":
    case "planning":
    case "engagement-acceptance":
    case "fraud-risk":
    case "it-risk":
    case "materiality":
    case "preliminary-analytical":
    case "understanding-entity":
      const initialTab = activeSection === "planning" || activeSection === "planning-overview" ? "dashboard" : activeSection;
      return <PlanningWorkflow initialTab={initialTab} />;

    // New planning landing pages
    case "planning-engagement-acceptance":
      if (!setActiveSection) return <EngagementAcceptanceLanding onSelect={() => { }} />;
      return <EngagementAcceptanceLanding onSelect={setActiveSection} />;

    case "planning-understanding-entity":
      if (!setActiveSection) return <UnderstandingEntityLanding onSelect={() => { }} />;
      return <UnderstandingEntityLanding onSelect={setActiveSection} />;



    case "planning-fraud-risk":
      if (!setActiveSection) return <FraudRiskLanding onSelect={() => { }} />;
      return <FraudRiskLanding onSelect={setActiveSection} />;

    case "planning-it-risk":
      if (!setActiveSection) return <ITRiskLanding onSelect={() => { }} />;
      return <ITRiskLanding onSelect={setActiveSection} />;

    // Engagement Acceptance Sub-sections
    case "engagement-acceptance-engagement-profiling":
      return <PlanningSection sectionKey="engagement-acceptance" title="Engagement Profiling" initialTab="Engagement Profiling" />;

    case "engagement-acceptance-compliance-ethical-requirements":
      return <PlanningSection sectionKey="engagement-acceptance" title="Compliance with Ethical Requirements" initialTab="Compliance with Ethical Requirements" />;

    case "engagement-acceptance-scope-of-review":
      return <PlanningSection sectionKey="engagement-acceptance" title="Scope of Review" initialTab="Scope of Review" />;

    case "engagement-acceptance-assess-engagement-risk":
      return <PlanningSection sectionKey="engagement-acceptance" title="Assess Engagement Risk" initialTab="Assess Engagement Risk" />;

    case "engagement-acceptance-agreeing-terms-audit-engagement":
      return <PlanningSection sectionKey="engagement-acceptance" title="Agreeing the Terms of Audit Engagement" initialTab="Agreeing the Terms of Audit Engagement" />;

    // Understanding Entity Sub-sections
    case "understanding-entity-external-factors":
      return <PlanningSection sectionKey="understanding-entity" title="External Factors" initialTab="External Factors" />;

    case "understanding-entity-internal-factors":
      return <PlanningSection sectionKey="understanding-entity" title="Internal Factors" initialTab="Internal Factors" />;

    case "understanding-entity-accounting-policies":
      return <PlanningSection sectionKey="understanding-entity" title="Accounting Policies" initialTab="Accounting Policies" />;

    case "understanding-entity-other-considerations":
      return <PlanningSection sectionKey="understanding-entity" title="Other Considerations" initialTab="Other Considerations" />;

    case "understanding-entity-understanding-internal-controls":
      return <PlanningSection sectionKey="understanding-entity" title="Understanding Internal Controls" initialTab="Understanding Internal Controls" />;

    // Materiality Sub-sections
    case "materiality-overall-materiality":
      return <PlanningSection sectionKey="materiality" title="Overall Materiality" initialTab="Overall Materiality" />;

    case "materiality-performance-materiality":
      return <PlanningSection sectionKey="materiality" title="Performance Materiality" initialTab="Performance Materiality" />;

    case "materiality-component-materiality":
      return <PlanningSection sectionKey="materiality" title="Component Materiality" initialTab="Component Materiality" />;

    case "materiality-documentation":
      return <PlanningSection sectionKey="materiality" title="Documentation" initialTab="Documentation" />;

    case "materiality-revision":
      return <PlanningSection sectionKey="materiality" title="Materiality Revision" initialTab="Materiality Revision" />;

    case "materiality-approval":
      return <PlanningSection sectionKey="materiality" title="Approval" initialTab="Approval" />;

    // Preliminary Analytical Sub-sections
    case "preliminary-analytical-ratio-analysis":
      return <PlanningSection sectionKey="preliminary-analytical" title="Ratio Analysis" initialTab="Ratio Analysis" />;

    case "preliminary-analytical-trend-analysis":
      return <PlanningSection sectionKey="preliminary-analytical" title="Trend Analysis" initialTab="Trend Analysis" />;

    case "preliminary-analytical-benchmarking":
      return <PlanningSection sectionKey="preliminary-analytical" title="Benchmarking" initialTab="Benchmarking" />;

    case "preliminary-analytical-documentation":
      return <PlanningSection sectionKey="preliminary-analytical" title="Documentation" initialTab="Documentation" />;

    case "preliminary-analytical-risk-assessment":
      return <PlanningSection sectionKey="preliminary-analytical" title="Risk Assessment" initialTab="Risk Assessment" />;

    case "preliminary-analytical-approval":
      return <PlanningSection sectionKey="preliminary-analytical" title="Approval" initialTab="Approval" />;

    // Fraud Risk Sub-sections
    case "fraud-risk-inquiries-management":
      return <PlanningSection sectionKey="fraud-risk" title="Inquiries of the management and others within the entity" initialTab="Inquiries of the management and others within the entity" />;

    case "fraud-risk-planning-discussion":
      return <PlanningSection sectionKey="fraud-risk" title="Planning Discussion with the Client/Entity" initialTab="Planning Discussion with the Client/Entity" />;

    case "fraud-risk-fraud-risk-factors":
      return <PlanningSection sectionKey="fraud-risk" title="Fraud Risk Factors" initialTab="Fraud Risk Factors" />;

    case "fraud-risk-management-override":
      return <PlanningSection sectionKey="fraud-risk" title="Management Override of Controls" initialTab="Management Override of Controls" />;

    case "fraud-risk-communication-management":
      return <PlanningSection sectionKey="fraud-risk" title="Communication with management and those charged with governance regarding matters of fraud" initialTab="Communication with management and those charged with governance regarding matters of fraud" />;

    // IT Risk Sub-sections
    case "it-risk-it-risk-assessment":
      return <PlanningSection sectionKey="it-risk" title="IT Risk Assessment" initialTab="IT Risk Assessment" />;

    // Generic handler for all other deficiency routes
    default:
      if (activeSection.endsWith("-deficiencies")) {
        const workspaceName = Object.keys(WORKSPACE_DEFICIENCY_ROUTES).find(
          (name) => WORKSPACE_DEFICIENCY_ROUTES[name].routeId === activeSection
        );
        if (workspaceName) {
          const routeConfig = WORKSPACE_DEFICIENCY_ROUTES[workspaceName];
          return (
            <WorkspaceDeficiencies
              workspaceName={workspaceName}
            />
          );
        }
      }
      return null;
  }
}
